import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { IModuleDetail } from 'src/app/interfaces/imodule-detail';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { ActivatedRoute } from '@angular/router';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { NavigationEnd, Router } from '@angular/router';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';

@Component({
  selector: 'app-reporting-visualization',
  templateUrl: './reporting-visualization.component.html',
  styleUrls: ['./reporting-visualization.component.scss'],
  standalone: false,
  providers: [
        {
          provide: TRANSLOCO_SCOPE,
          useValue: 'reporting',
        },
      ],
})
export class ReportingVisualizationComponent {
  @ViewChild('tabWrapper') tabWrapper!: ElementRef;
  isExpanded: boolean = false;
  moduleUuid: string = '';
  moduleName: string = '';
  moduleDetail: IModuleDetail | undefined;
  currentTab = "Certifications";
  isModuleLoading: boolean = true;
  noData: boolean = false;
  position: number | undefined;
  previousExpanded: boolean = false;
  accountUuid: string = '';
  callTypesInModule: string = '';
  certificationAssignmentCount: number = 0;
  practiceAssignmentCount: number = 0;

  constructor(
    private reportingService: ReportingService,
    private route: ActivatedRoute,
    private gaService: GoogleAnalyticsService,
    private router: Router,
    private responseTimeService: ResponseTimeService,
    private amplitudeService: AmplitudeService,
    private translationService: TranslationService
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reporting');
    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    this.accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.gaService.trackPageView(event.urlAfterRedirects);
      }
    });
    this.route.queryParams.subscribe((params) => {
      this.moduleUuid = params['moduleUuid'];
      if (this.moduleUuid) {
        this.getModuleDetails();
      } else {
        // Sending the Event of Module Report Page View to Amplitude
        this.amplitudeService.trackEvent('modulereport_pageview', {
          'module_name': 'null',
          'module_uuid': 'null'
        });
        console.error('Module UUID is missing');
      }
    });
  }

  getModuleDetails() {
    this.responseTimeService.logResponseTime(
      this.reportingService.getModuleDetails(this.accountUuid, this.moduleUuid),
      'getModuleDetails'
    ).subscribe({
      next: (res: IModuleDetail) => {
        if (!res || Object.keys(res).length === 0) {
          this.noData = true;
        }
        // Sending the Event of Module Report Page View to Amplitude
        this.amplitudeService.trackEvent('modulereport_pageview', {
          'module_name': res?.moduleName,
          'module_uuid': this.moduleUuid
        });

        this.moduleDetail = res;
        this.moduleName = res?.moduleName;
        this.callTypesInModule = res?.callTypesInModule;
        this.isModuleLoading = false;
        this.certificationAssignmentCount = res?.certificationAssignmentCount;
        this.practiceAssignmentCount = res?.practiceAssignmentCount;
        if (this.certificationAssignmentCount > 0) {
          this.currentTab = "Certifications";
        } else if (this.practiceAssignmentCount > 0) {
          this.currentTab = "Practices";
        } else {
          this.currentTab = "Content";
        }
      },
      error: (error) => {
        console.error('Error fetching module details', error);
      }
    });
  }

  getTabClass(tabName: string): string {
    if (this.currentTab === tabName) {
      return 'active';
    }
    return this.isExpanded ? 'expanded-non-active' : '';
  }

  setTab(tabName: string): void {
    switch (tabName) {
      case 'Certifications':
        // Sending the Event of Clicking 'Certifications' Tab to Amplitude
        this.amplitudeService.trackEvent('modulereport_click_certifications');
        break;
      case 'Content':
        // Sending the Event of Clicking 'Call Analysis' Tab to Amplitude
        this.amplitudeService.trackEvent('modulereport_click_callanalysis');
        break;
    }

    this.currentTab = tabName;
    this.previousExpanded = this.isExpanded;
    this.gaService.eventEmitter('tab clicked');

  }

  navigateToRoute() {
    this.router.navigate(['/reporting']);
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    if (!this.tabWrapper) {
      return;
    }
    const tabPosition = this.tabWrapper.nativeElement.getBoundingClientRect().top;
    this.position = tabPosition;
    if (tabPosition <= 20) {
      this.isExpanded = true;
    } else {
      this.isExpanded = false;
    }
  }

  maintainScrollPosition() {
    setTimeout(() => {
      if (this.previousExpanded && !this.isExpanded) {
        this.tabWrapper.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }
}
